"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { API, clearApiCache } from "@/lib/api";
import type { ApiError } from "@/lib/api";
import type { PlatformRole, TenantMembershipResponse, TenantRole } from "@/lib/api";
import type { Role } from "@/types";
import {
  hasAdminAccess,
  hasInstructorAccess,
  hasPlatformAdminAccess,
  hasPlatformOwnerAccess,
  hasSchedulingAccess,
  hasTenantAdminAccess,
} from "@/lib/auth/roleAccess";
import { log } from "@/lib/log";
import { authContinuation } from '@/lib/auth/continuation';

interface User {
  id: string;
  name: string;
  email: string;
  roles: Role[];
  platformRoles: PlatformRole[];
  activeTenantId?: string | null;
  tenantRoles: TenantRole[];
}

interface AuthContextValue {
  user: User | null;
  tenants: TenantMembershipResponse[];
  activeTenant: TenantMembershipResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isTenantSelectionRequired: boolean;
  login: (email: string, password: string, destination?: string) => Promise<void>;
  completeSsoLogin: (data: SsoSession, destination?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshTenants: () => Promise<TenantMembershipResponse[]>;
  selectTenant: (tenantId: string) => Promise<void>;
  hasRole: (role: Role | Role[]) => boolean;
  hasPlatformRole: (role: PlatformRole | PlatformRole[]) => boolean;
  hasTenantRole: (role: TenantRole | TenantRole[]) => boolean;
  isAdmin: () => boolean;
  isInstructor: () => boolean;
  isScheduler: () => boolean;
  isOwner: () => boolean;
  isPlatformAdmin: () => boolean;
  isTenantAdmin: () => boolean;
}

interface SsoSession {
  token: string;
  user_id: string;
  email: string;
  name: string;
}

interface TokenPayload {
  sub: string;
  exp?: number;
  name?: string;
  email?: string;
  roles?: string[];
  platform_roles?: string[];
  tenant_roles?: string[];
  active_tenant_id?: string;
}

function isRole(value: string): value is Role {
  return ['Student', 'Instructor', 'Admin', 'Owner', 'Prospect'].includes(value);
}

function normalizeRoles(roles: string[] | undefined): Role[] {
  return (roles ?? [])
    .map((role) => role.replace(/"/g, ''))
    .filter(isRole);
}

function isPlatformRole(value: string): value is PlatformRole {
  return ['PlatformOwner', 'PlatformAdmin', 'Support', 'BillingOps'].includes(value);
}

function normalizePlatformRoles(roles: string[] | undefined): PlatformRole[] {
  return (roles ?? [])
    .map((role) => role.replace(/"/g, ''))
    .filter(isPlatformRole);
}

function isTenantRole(value: string): value is TenantRole {
  return [
    'InstitutionAdmin',
    'Instructor',
    'Student',
    'Guardian',
    'FinanceManager',
    'ContentManager',
  ].includes(value);
}

function normalizeTenantRoles(roles: string[] | undefined): TenantRole[] {
  return (roles ?? [])
    .map((role) => role.replace(/"/g, ''))
    .filter(isTenantRole);
}

function decodeTokenPayload(token: string): TokenPayload {
  const parts = token.split(".");

  // Test/mock-only token shape (produced by the MSW mock layer). Gated out of
  // production builds so a planted `mock.jwt.token.*` value in localStorage can
  // never satisfy auth in a real deployment.
  if (
    process.env.NODE_ENV !== "production" &&
    parts.length === 4 &&
    parts[0] === "mock" &&
    parts[1] === "jwt" &&
    parts[2] === "token"
  ) {
    return { sub: parts[3], roles: [] };
  }

  if (parts.length !== 3) {
    throw new Error("Invalid token format");
  }

  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - base64.length % 4) % 4), "=");
  return JSON.parse(atob(padded)) as TokenPayload;
}

function isApiError(error: unknown): error is ApiError {
  return typeof error === "object" && error !== null && "status" in error;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  tenants: [],
  activeTenant: null,
  isLoading: true,
  isAuthenticated: false,
  isTenantSelectionRequired: false,
  login: async () => {},
  completeSsoLogin: async () => {},
  logout: async () => {},
  refreshTenants: async () => [],
  selectTenant: async () => {},
  hasRole: () => false,
  hasPlatformRole: () => false,
  hasTenantRole: () => false,
  isAdmin: () => false,
  isInstructor: () => false,
  isScheduler: () => false,
  isOwner: () => false,
  isPlatformAdmin: () => false,
  isTenantAdmin: () => false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<TenantMembershipResponse[]>([]);
  const [activeTenant, setActiveTenant] = useState<TenantMembershipResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  const applyTenantState = (
    memberships: TenantMembershipResponse[],
    activeTenantId?: string | null
  ) => {
    const isSelectableTenant = (tenant: TenantMembershipResponse) =>
      tenant.tenant_status === "Active" || tenant.tenant_status === "Trial";
    const active =
      memberships.find((tenant) => tenant.tenant_id === activeTenantId) ??
      (memberships.length === 1 && isSelectableTenant(memberships[0]) ? memberships[0] : null);
    setTenants(memberships);
    setActiveTenant(active);
    return active;
  };

  const refreshTenants = async () => {
    const token = localStorage.getItem("auth_token");
    const payload = token ? decodeTokenPayload(token) : null;
    const memberships = await API.auth.listTenants('AuthContext-refreshTenants');
    applyTenantState(memberships, payload?.active_tenant_id);
    return memberships;
  };

  // Check if user is logged in on first render
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const payload = decodeTokenPayload(token);
        const expTime = payload.exp ? payload.exp * 1000 : 0; // Convert to milliseconds
        
        if (expTime && expTime < Date.now()) {
          throw new Error("Token expired");
        }
        
        // Get temporary user info from token
        const tempUser = {
          id: payload.sub,
          name: payload.name || "User",
          email: payload.email || "",
          roles: normalizeRoles(payload.roles),
          platformRoles: normalizePlatformRoles(payload.platform_roles),
          activeTenantId: payload.active_tenant_id ?? null,
          tenantRoles: normalizeTenantRoles(payload.tenant_roles),
        };
        
        // Fetch complete user data from API
        try {
          const userData = await API.users.getById(tempUser.id, 'AuthContext-checkAuth');
          // Update user with complete data from API
          setUser({
            id: userData.id || tempUser.id,
            name: userData.name || tempUser.name,
            email: userData.email || tempUser.email,
            roles: normalizeRoles(userData.roles).length > 0 ? normalizeRoles(userData.roles) : tempUser.roles,
            platformRoles: normalizePlatformRoles(userData.platform_roles).length > 0
              ? normalizePlatformRoles(userData.platform_roles)
              : tempUser.platformRoles,
            activeTenantId: tempUser.activeTenantId,
            tenantRoles: tempUser.tenantRoles,
          });
        } catch (apiError: unknown) {
          log.error("Error fetching user data:", apiError);
          // If this is also an auth error, don't show another toast - we'll handle it in the catch block below
          if (isApiError(apiError) && apiError.status === 401) {
            throw new Error("Token expired during API call");
          }
          // Fallback to token data if API call fails for other reasons
          setUser(tempUser);
        }

        try {
          const memberships = await API.auth.listTenants('AuthContext-checkAuth-tenants');
          applyTenantState(memberships, payload.active_tenant_id);
        } catch (tenantError) {
          log.error("Error fetching tenant memberships:", tenantError);
          setTenants([]);
          setActiveTenant(null);
        }
      } catch (error) {
        log.error("Error verifying token:", error);
        localStorage.removeItem("auth_token");
        setUser(null);
        setTenants([]);
        setActiveTenant(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string, destination?: string) => {
    setIsLoading(true);
    try {
      const data = await API.auth.login(email, password, 'AuthContext-login');
      localStorage.setItem("auth_token", data.token);
      const payload = decodeTokenPayload(data.token);
      
      // Set user data
      setUser({
        id: data.id,
        name: data.name,
        email: data.email,
        roles: data.roles,
        platformRoles: data.platform_roles ?? normalizePlatformRoles(payload.platform_roles),
        activeTenantId: payload.active_tenant_id ?? null,
        tenantRoles: normalizeTenantRoles(payload.tenant_roles),
      });

      const memberships = await API.auth.listTenants('AuthContext-login-tenants');
      const active = applyTenantState(memberships, payload.active_tenant_id);
      const safeDestination = authContinuation(destination);
      router.push(safeDestination ?? (active ? "/dashboard" : "/dashboard/select-tenant"));
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Establish a session from an SSO callback result. The token is already
  // tenant-scoped, so this mirrors `login` minus the password exchange: store
  // the token, hydrate the user from it, load memberships, and route.
  const completeSsoLogin = async (data: SsoSession, destination?: string) => {
    setIsLoading(true);
    try {
      localStorage.setItem("auth_token", data.token);
      clearApiCache();
      const payload = decodeTokenPayload(data.token);

      setUser({
        id: data.user_id,
        name: data.name,
        email: data.email,
        roles: normalizeRoles(payload.roles),
        platformRoles: normalizePlatformRoles(payload.platform_roles),
        activeTenantId: payload.active_tenant_id ?? null,
        tenantRoles: normalizeTenantRoles(payload.tenant_roles),
      });

      const memberships = await API.auth.listTenants('AuthContext-sso-login-tenants');
      const active = applyTenantState(memberships, payload.active_tenant_id);
      const safeDestination = authContinuation(destination);
      router.push(safeDestination ?? (active ? "/dashboard" : "/dashboard/select-tenant"));
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      if (token) {
        await API.auth.logout('AuthContext-logout');
      }
    } catch (error) {
      log.error("Logout error:", error);
    } finally {
      localStorage.removeItem("auth_token");
      setUser(null);
      setTenants([]);
      setActiveTenant(null);
      setIsLoading(false);
      router.push("/login");
    }
  };

  const selectTenant = async (tenantId: string) => {
    setIsLoading(true);
    try {
      const selection = await API.auth.selectTenant(tenantId, 'AuthContext-selectTenant');
      localStorage.setItem("auth_token", selection.token);
      clearApiCache();
      const payload = decodeTokenPayload(selection.token);
      setActiveTenant(selection.tenant);
      setTenants((current) => {
        if (current.some((tenant) => tenant.tenant_id === selection.tenant.tenant_id)) {
          return current.map((tenant) =>
            tenant.tenant_id === selection.tenant.tenant_id ? selection.tenant : tenant
          );
        }
        return [...current, selection.tenant];
      });
      setUser((current) => current ? {
        ...current,
        activeTenantId: payload.active_tenant_id ?? selection.tenant.tenant_id,
        platformRoles: normalizePlatformRoles(payload.platform_roles).length > 0
          ? normalizePlatformRoles(payload.platform_roles)
          : current.platformRoles,
        tenantRoles: normalizeTenantRoles(payload.tenant_roles),
      } : current);
      router.push("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const hasRole = (roleCheck: Role | Role[]) => {
    if (!user) return false;
    
    const rolesToCheck = Array.isArray(roleCheck) ? roleCheck : [roleCheck];
    return user.roles.some(role => rolesToCheck.includes(role));
  };

  const hasPlatformRole = (roleCheck: PlatformRole | PlatformRole[]) => {
    if (!user) return false;
    const rolesToCheck = Array.isArray(roleCheck) ? roleCheck : [roleCheck];
    return user.platformRoles.some(role => rolesToCheck.includes(role));
  };

  const hasTenantRole = (roleCheck: TenantRole | TenantRole[]) => {
    if (!user) return false;
    const rolesToCheck = Array.isArray(roleCheck) ? roleCheck : [roleCheck];
    const currentRoles = activeTenant?.roles ?? user.tenantRoles;
    return currentRoles.some(role => rolesToCheck.includes(role));
  };

  // Helper function for institution-scoped admin access.
  const isAdmin = () => {
    const currentRoles = activeTenant?.roles ?? user?.tenantRoles ?? [];
    return hasAdminAccess(user, currentRoles);
  };

  // Helper function for instructor-level access (Instructor, Admin, or Owner)
  const isInstructor = () => {
    const currentRoles = activeTenant?.roles ?? user?.tenantRoles ?? [];
    return hasInstructorAccess(user, currentRoles);
  };

  // Who may manage live-class scheduling/attendance — mirrors the backend
  // SCHEDULING_ROLES (excludes ContentManager, unlike isInstructor).
  const isScheduler = () => {
    const currentRoles = activeTenant?.roles ?? user?.tenantRoles ?? [];
    return hasSchedulingAccess(user, currentRoles);
  };

  const isOwner = () => {
    return hasPlatformOwnerAccess(user);
  };

  const isPlatformAdmin = () => hasPlatformAdminAccess(user);

  const isTenantAdmin = () => {
    const currentRoles = activeTenant?.roles ?? user?.tenantRoles ?? [];
    return hasTenantAdminAccess(user, currentRoles);
  };

  const isTenantSelectionRequired = !!user && tenants.length > 0 && !activeTenant;

  return (
    <AuthContext.Provider
      value={{
        user,
        tenants,
        activeTenant,
        isLoading,
        isAuthenticated: !!user,
        isTenantSelectionRequired,
        login,
        completeSsoLogin,
        logout,
        refreshTenants,
        selectTenant,
        hasRole,
        hasPlatformRole,
        hasTenantRole,
        isAdmin,
        isInstructor,
        isScheduler,
        isOwner,
        isPlatformAdmin,
        isTenantAdmin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
